import type { FC, ReactNode } from 'react'
import type { Post } from '@/lib/posts'

import { cn } from '@/lib/utils'
import { tintClass } from '@/lib/tints'
import { Snippet } from '@/components/art/snippets'
import { Photo, photoForName } from '@/components/generals/photo'

type CoverArtKind = 'job-board' | 'portfolio' | 'referral' | 'interview' | 'diary' | 'offer'

const KINDS: CoverArtKind[] = ['job-board', 'portfolio', 'referral', 'interview', 'diary', 'offer']

const BY_SLUG: Record<string, CoverArtKind> = {
    '5-tips-for-hiring-designers-in-2026': 'job-board',
    'what-makes-a-portfolio-worth-meeting': 'portfolio',
    'why-referrals-beat-inbound-for-design-roles': 'referral',
    'how-to-run-a-design-interview-that-tests-craft': 'interview',
    'a-scouts-diary-ten-portfolios-in-one-night': 'diary',
    'offers-designers-say-yes-to': 'offer'
}

const kindFor = (post: Post): CoverArtKind => {
    if (BY_SLUG[post.slug]) return BY_SLUG[post.slug]

    const hash = post.slug.split('').reduce((total, character) => (total * 31 + character.charCodeAt(0)) >>> 0, 3)

    return KINDS[hash % KINDS.length]
}

const Bar: FC<{ className: string }> = ({ className }): ReactNode => <span className={cn('block h-[0.7cqw] min-h-[2px] rounded-full bg-ink/15', className)} />

/** A job-board sheet with one row pulled forward and circled, a polaroid, and a scribble. */
const JobBoardArt: FC = (): ReactNode => {
    return (
        <>
            <div className='absolute left-[7%] top-[11%] w-[58%] rounded-[1cqw] bg-paper p-[3cqw] shadow-[0_1px_2px_rgb(40_38_34/0.12),0_10px_24px_-14px_rgb(40_38_34/0.4)] [rotate:-1deg]'>
                <p className='font-serif text-[3.4cqw] leading-none text-ink'>Open roles</p>
                <div className='mt-[2.6cqw] flex flex-col gap-[1.6cqw]'>
                    {[0, 1, 2, 3, 4].map(row => {
                        const pulled = row === 2

                        return (
                            <div
                                key={row}
                                className={cn(
                                    'relative flex items-center justify-between gap-[2cqw] rounded-[0.8cqw] px-[1.8cqw] py-[1.5cqw]',
                                    pulled ? 'translate-x-[3cqw] bg-[#fbe9e2] shadow-[0_8px_16px_-8px_rgb(201_89_47/0.45),0_0_0_1px_rgb(201_89_47/0.35)]' : 'bg-[#f6f2ec]'
                                )}
                            >
                                <div className='flex flex-1 flex-col gap-[0.9cqw]'>
                                    <Bar className={cn('w-[60%]', pulled && 'bg-clay/60')} />
                                    <Bar className='w-[40%]' />
                                </div>
                                <span className={cn('relative rounded-full px-[1.6cqw] py-[0.6cqw] text-[1.6cqw] leading-none', pulled ? 'bg-clay text-paper' : 'bg-ink/10 text-ink/50')}>
                                    Apply
                                    {pulled && (
                                        <svg viewBox='0 0 100 60' className='absolute -inset-x-[70%] -inset-y-[110%] overflow-visible'>
                                            <path d='M12 30 C 10 8 78 2 90 22 C 100 44 40 58 18 46 C 6 38 14 20 30 14' fill='none' stroke='#c9592f' strokeWidth='2.2' strokeLinecap='round' />
                                        </svg>
                                    )}
                                </span>
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className='absolute right-[7%] top-[17%] w-[29%] bg-paper p-[1.6cqw] pb-[5cqw] shadow-[0_1px_2px_rgb(40_38_34/0.14),0_12px_26px_-14px_rgb(40_38_34/0.45)] [rotate:3deg]'>
                <Photo id='sofia' sizes='200px' className='aspect-[4/5] w-full' />
                <p className='hand mt-[1.4cqw] text-center text-[2.8cqw] text-ink/70 leading-[1.05]'>the one</p>
            </div>

            <p className='hand absolute bottom-[8%] right-[9%] text-[3.6cqw] text-ink/60 [rotate:-8deg] leading-[1.05]'>
                worth a call, today
            </p>
        </>
    )
}

const PortfolioArt: FC = (): ReactNode => {
    return (
        <>
            <div className='absolute left-[12%] top-[14%] w-[30%] [rotate:-6deg]'>
                <Snippet kind='poster' />
            </div>
            <div className='absolute left-[38%] top-[20%] w-[28%] [rotate:4deg]'>
                <Snippet kind='type' />
            </div>
            <div className='absolute right-[9%] top-[12%] w-[24%] [rotate:-3deg]'>
                <Snippet kind='screen' />
            </div>
            <div className='absolute bottom-[10%] right-[16%] w-[24%] bg-[#f4dc9a] p-[2cqw] shadow-[0_8px_16px_-10px_rgb(40_38_34/0.5)] [rotate:5deg]'>
                <p className='hand text-[3.2cqw] leading-[1.1] text-ink/75'>worth meeting. calm, exact.</p>
            </div>
        </>
    )
}

const PRINT = 'absolute bottom-[10%] w-[25%] bg-paper p-[1.2cqw] shadow-[0_1px_2px_rgb(40_38_34/0.14),0_12px_26px_-14px_rgb(40_38_34/0.45)]'

const ReferralArt: FC<{ author: string }> = ({ author }): ReactNode => {
    const scout = photoForName(author)
    const designer = scout === 'mateo' ? 'adrian' : 'mateo'

    return (
        <>
            <div className={cn(PRINT, 'left-[12%] [rotate:-4deg]')}>
                <Photo id={scout} sizes='200px' className='aspect-square w-full' />
            </div>
            <div className={cn(PRINT, 'right-[12%] [rotate:4deg]')}>
                <Photo id={designer} sizes='200px' className='aspect-square w-full' />
            </div>
            <svg viewBox='0 0 300 80' className='absolute left-[30%] top-[22%] w-[40%] overflow-visible'>
                <path d='M10 60 C 80 0 220 0 290 60' fill='none' stroke='#282622' strokeOpacity='0.45' strokeWidth='2' strokeDasharray='6 7' strokeLinecap='round' />
            </svg>
            <p className='hand absolute left-1/2 top-[11%] -translate-x-1/2 whitespace-nowrap text-[3.8cqw] text-ink/70 [rotate:-3deg] leading-[1.05]'>
                you two should talk
            </p>
        </>
    )
}

const InterviewArt: FC = (): ReactNode => {
    return (
        <>
            <div className='absolute left-[16%] top-[10%] w-[52%] bg-paper p-[3.4cqw] shadow-[0_1px_2px_rgb(40_38_34/0.12),0_12px_26px_-14px_rgb(40_38_34/0.4)] [rotate:-2deg]'>
                <p className='font-serif text-[3.2cqw] leading-none text-ink'>Interview notes</p>
                <div className='mt-[2.6cqw] flex flex-col gap-[2cqw]'>
                    {['Walked us through the real files', 'Asked about our users first', 'Named what she would cut'].map((line, index) => (
                        <div key={line} className='flex items-center gap-[1.6cqw]'>
                            <span className={cn('flex size-[2.6cqw] items-center justify-center rounded-[0.5cqw] border border-ink/40', index < 2 && 'bg-ink/80')} />
                            <span className='hand text-[2.6cqw] leading-none text-ink/75'>{line}</span>
                        </div>
                    ))}
                </div>
            </div>
            <svg viewBox='0 0 200 24' className='absolute bottom-[16%] right-[10%] w-[34%] overflow-visible [rotate:-24deg]'>
                <rect x='0' y='6' width='160' height='12' rx='2' fill='#e2b860' />
                <rect x='150' y='6' width='22' height='12' fill='#6f9bd1' />
                <path d='M0 6 L -18 12 L 0 18 Z' transform='translate(0 0)' fill='#f1d9b0' />
                <rect x='172' y='6' width='14' height='12' rx='3' fill='#282622' opacity='0.6' />
            </svg>
        </>
    )
}

const DiaryArt: FC = (): ReactNode => {
    return (
        <>
            <div className='absolute left-[18%] top-[8%] h-[84%] w-[50%] bg-paper shadow-[0_1px_2px_rgb(40_38_34/0.12),0_12px_26px_-14px_rgb(40_38_34/0.4)] [rotate:1.5deg]'>
                <div className='absolute inset-0 [background-image:repeating-linear-gradient(to_bottom,transparent_0,transparent_calc(5.6cqw_-_1px),rgb(40_38_34/0.12)_calc(5.6cqw_-_1px),rgb(40_38_34/0.12)_5.6cqw)] [background-position:0_9cqw]' />
                <div className='absolute inset-y-0 left-[12%] w-px bg-azure/50' />
                <p className='hand absolute left-[17%] top-[12%] text-[3.4cqw] text-ink/80 leading-[1.05]'>Tuesday</p>
                <p className='hand absolute left-[17%] top-[30%] text-[3cqw] text-ink/70 leading-[1.05]'>10 portfolios, 1 night</p>
                <p className='hand absolute left-[17%] top-[46%] text-[3cqw] text-ink/70 leading-[1.05]'>no. 5 ... yes!</p>
            </div>
            <div className='absolute bottom-[12%] right-[10%] size-[18cqw] rounded-full border-[0.9cqw] border-[#b08a6a]/35' />
        </>
    )
}

const OfferArt: FC = (): ReactNode => {
    return (
        <>
            <div className='absolute left-[22%] top-[10%] w-[50%] bg-paper p-[3.4cqw] shadow-[0_1px_2px_rgb(40_38_34/0.12),0_12px_26px_-14px_rgb(40_38_34/0.4)] [rotate:-1.5deg]'>
                <p className='font-serif text-[3.4cqw] leading-none text-ink'>Offer letter</p>
                <div className='mt-[2.8cqw] flex flex-col gap-[1.4cqw]'>
                    <Bar className='w-[90%]' />
                    <Bar className='w-[80%]' />
                    <Bar className='w-[86%]' />
                    <Bar className='w-[50%]' />
                </div>
                <p className='hand mt-[4cqw] text-[3.4cqw] text-ink/80 leading-[1.05]'>Ada L.</p>
                <span className='mt-[0.6cqw] block h-px w-[45%] bg-ink/25' />
            </div>
            <div className='absolute bottom-[14%] right-[18%] flex size-[16cqw] items-center justify-center rounded-full border-[0.4cqw] border-clay/70 text-clay [rotate:-10deg]'>
                <span className='text-[2cqw] font-semibold uppercase tracking-[0.16em]'>Accepted</span>
            </div>
        </>
    )
}

interface PostCoverProps {
    post: Post
    className?: string
    priority?: boolean
}

/** The recessed window holding the post's cover: the CMS image when there is one, otherwise drawn artwork. */
export const PostWindow: FC<PostCoverProps> = ({ post, className, priority = false }): ReactNode => {
    const kind = kindFor(post)

    return (
        <div className={cn('card-window [container-type:inline-size]', tintClass[post.tint], className)}>
            {post.coverImage ? (
                <img
                    src={post.coverImage}
                    alt={post.coverImageAlt ?? ''}
                    loading={priority ? 'eager' : 'lazy'}
                    className='absolute inset-0 size-full object-cover'
                />
            ) : (
                <div aria-hidden='true' className='absolute inset-0'>
                    {kind === 'job-board' && <JobBoardArt />}
                    {kind === 'portfolio' && <PortfolioArt />}
                    {kind === 'referral' && <ReferralArt author={post.authorName} />}
                    {kind === 'interview' && <InterviewArt />}
                    {kind === 'diary' && <DiaryArt />}
                    {kind === 'offer' && <OfferArt />}
                </div>
            )}
        </div>
    )
}

/** The post's cover on a recessed mat, framed in the card anatomy. */
export const PostCover: FC<PostCoverProps> = ({ post, className, priority = false }): ReactNode => {
    return (
        <div className={cn('card-frame', className)}>
            <PostWindow post={post} priority={priority} className='h-full' />
        </div>
    )
}
